import { CalendarCheck, Compass, Cross, Fingerprint, Flower2, Heart, Sprout, Users, Waves } from "lucide-react";
import { Eyebrow, GoldLine, Reveal, WhatsAppButton } from "@/components/bits";

/* --------------------------- 5. METODO (3 FASI) -------------------------- */
const fasi = [
  {
    num: "01",
    name: "Cuore",
    claim: "Ricostruire ciò che porti dentro.",
    pratica: "Comprendere ciò che influenza le proprie emozioni, convinzioni e decisioni.",
    items: [
      "identità", "ferite", "autostima", "paura", "vergogna", "bisogno di approvazione",
      "controllo", "senso di colpa", "rapporto con il passato", "rapporto con Dio",
      "percezione di te stessa", "convinzioni che guidano le tue scelte",
    ],
    frase: "Prima di cambiare ciò che fai, devi comprendere da dove stai vivendo.",
  },
  {
    num: "02",
    name: "Governo",
    claim: "Imparare a guidare ciò che senti.",
    pratica: "Sviluppare comunicazione, confini personali, disciplina e responsabilità.",
    items: [
      "emozioni", "pensieri", "reazioni", "comunicazione", "confini", "disciplina",
      "abitudini", "corpo", "gestione del tempo", "responsabilità", "scelte quotidiane",
      "autocontrollo", "capacità di fermarsi prima di reagire",
    ],
    frase: "Le emozioni sono importanti. Ma non devono diventare il tuo padrone.",
  },
  {
    num: "03",
    name: "Frutto",
    claim: "Portare il cambiamento nella vita reale.",
    pratica: "Applicare il cambiamento alle relazioni, alle abitudini e alla vita quotidiana.",
    items: [
      "matrimonio", "coppia", "relazioni", "famiglia", "figli", "maternità", "amicizie",
      "lavoro", "femminilità", "responsabilità", "chiamata", "vita quotidiana",
      "modo in cui comunichi", "modo in cui ami",
      "modo in cui ti prendi cura di ciò che Dio ti ha affidato",
    ],
    frase: "La trasformazione interiore deve produrre frutto nella vita che vivi.",
  },
];

export const Metodo = () => (
  <section id="metodo" data-testid="metodo-section" className="scroll-mt-24">
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
      <div className="max-w-3xl">
        <Reveal>
          <Eyebrow>Cuore → Governo → Frutto</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight text-[#1A1818] md:text-5xl">
            Il Metodo Donna Virtuosa
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-base leading-relaxed text-[#3A3532] md:text-lg">
            Un percorso che parte da ciò che hai dentro e arriva fino al modo in cui vivi ogni
            giorno.
          </p>
        </Reveal>
      </div>
      <div className="mt-16 space-y-8 md:mt-24 md:space-y-12">
        {fasi.map((fase, i) => (
          <Reveal key={fase.num} delay={0.05}>
            <article
              data-testid={`metodo-fase-${fase.num}`}
              className={`grid grid-cols-1 gap-10 border border-[#2A2421]/10 p-8 transition-transform duration-500 hover:-translate-y-1 md:p-14 lg:grid-cols-12 ${
                i % 2 === 1 ? "bg-[#F4EFE6]" : "bg-[#FDFBF7]"
              }`}
            >
              <div className="lg:col-span-4">
                <p className="font-serif text-6xl text-[#D4AF37] md:text-7xl">{fase.num}</p>
                <h3 className="mt-4 font-serif text-3xl uppercase tracking-[0.12em] text-[#1A1818] md:text-4xl">
                  {fase.name}
                </h3>
                <p className="mt-4 font-serif text-xl italic leading-snug text-[#3A3532]">
                  {fase.claim}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-[#3A3532]/80 md:text-base">
                  {fase.pratica}
                </p>
              </div>
              <div className="lg:col-span-8">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#3A3532]/60">
                  Lavoriamo su
                </p>
                <ul className="mt-5 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                  {fase.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-baseline gap-3 border-b border-[#2A2421]/10 py-2.5 text-sm text-[#3A3532] md:text-base"
                    >
                      <span className="h-px w-3 shrink-0 translate-y-[-4px] bg-[#D4AF37]" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="mt-8 font-serif text-lg italic leading-snug text-[#1A1818] md:text-xl">
                  {fase.frase}
                </p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------- 6. AREE DI LAVORO (9) ------------------------- */
const aree = [
  { icon: Fingerprint, title: "Identità", text: "Chi sei quando smetti di definirti attraverso ciò che ti è successo, ciò che hai perso o ciò che gli altri pensano di te?" },
  { icon: Waves, title: "Emozioni", text: "Imparare a riconoscere ciò che senti senza reprimere tutto e senza permettere alle emozioni di prendere ogni decisione al posto tuo." },
  { icon: Flower2, title: "Corpo", text: "Tornare a prenderti cura del corpo con rispetto, disciplina e responsabilità, senza viverlo come un nemico." },
  { icon: CalendarCheck, title: "Disciplina", text: "Costruire nuove abitudini e imparare a mantenere piccoli impegni quotidiani anche quando la motivazione scompare." },
  { icon: Users, title: "Relazioni", text: "Imparare a comunicare, mettere confini, perdonare e costruire relazioni più mature." },
  { icon: Heart, title: "Matrimonio", text: "Comprendere come essere presente nella coppia senza controllare, annullarti o perdere completamente la tua identità." },
  { icon: Sprout, title: "Maternità", text: "Essere madre senza dimenticare di essere anche donna. Imparare a gestire responsabilità, emozioni, senso di colpa e presenza." },
  { icon: Cross, title: "Fede", text: "Costruire una vita nella quale Dio non sia soltanto qualcuno che cerchi quando stai male. Ma il centro dal quale impari a vivere." },
  { icon: Compass, title: "Chiamata", text: "Comprendere i talenti, le responsabilità e la stagione che stai vivendo per iniziare a camminare con maggiore intenzionalità." },
];

export const Aree = () => (
  <section data-testid="aree-section" className="bg-[#F4EFE6]">
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
      <div className="max-w-3xl">
        <Reveal>
          <Eyebrow>Il lavoro, area per area</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight text-[#1A1818] md:text-5xl">
            Le aree su cui possiamo lavorare
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <GoldLine className="mt-10" />
        </Reveal>
      </div>
      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
        {aree.map((area, i) => {
          const Icon = area.icon;
          return (
            <Reveal key={area.title} delay={0.05 * (i % 3)}>
              <article
                data-testid={`area-card-${area.title.toLowerCase()}`}
                className="group flex h-full flex-col border border-[#2A2421]/10 bg-[#FDFBF7] p-8 transition-all duration-500 hover:-translate-y-1 hover:border-[#D4AF37]/60"
              >
                <Icon className="h-6 w-6 text-[#A98A2C] transition-transform duration-500 group-hover:scale-110" strokeWidth={1.25} />
                <h3 className="mt-6 font-serif text-2xl uppercase tracking-[0.1em] text-[#1A1818]">
                  {area.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-[#3A3532] md:text-base">{area.text}</p>
              </article>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ------------------------ 7. NON È UN CORSO REGISTRATO ------------------------ */
const nonCorsoLines = [
  "Il lavoro parte dalla tua storia.",
  "Dalla tua situazione.",
  "Dalle relazioni che stai vivendo.",
  "Dalle difficoltà che stai affrontando.",
  "Dalla stagione della tua vita.",
];

export const NonCorso = () => (
  <section data-testid="non-corso-section">
    <div className="mx-auto max-w-3xl px-6 py-24 md:px-10 md:py-36">
      <Reveal>
        <h2 className="font-serif text-3xl leading-tight tracking-tight text-[#1A1818] md:text-5xl">
          Non hai bisogno dell&rsquo;ennesimo video motivazionale.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-6 font-serif text-2xl italic leading-snug text-[#A98A2C] md:text-3xl">
          Hai bisogno di essere vista.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-10 space-y-2 text-base text-[#3A3532] md:text-lg">
          <p>Donna Virtuosa è un percorso individuale.</p>
          <p>Non sei un numero dentro un gruppo.</p>
          <p>Non ricevi semplicemente una serie di video da guardare.</p>
        </div>
      </Reveal>
      <div className="mt-8">
        {nonCorsoLines.map((line, i) => (
          <Reveal key={line} delay={0.04 * i}>
            <p className="flex items-baseline gap-3 border-b border-[#2A2421]/10 py-3 text-base text-[#3A3532] md:text-lg">
              <span className="h-px w-4 shrink-0 translate-y-[-5px] bg-[#D4AF37]" aria-hidden="true" />
              {line}
            </p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <p className="mt-10 text-base leading-relaxed text-[#3A3532] md:text-lg">
          Due donne possono manifestare lo stesso problema e avere due radici completamente
          differenti.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-6 text-base leading-relaxed text-[#3A3532] md:text-lg">
          Per questo Donna Virtuosa non parte da un protocollo uguale per tutte.
        </p>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-8 font-serif text-4xl uppercase tracking-[0.08em] text-[#1A1818] md:text-5xl">
          Parte da te.
        </p>
      </Reveal>
      <Reveal delay={0.25}>
        <div className="mt-12">
          <WhatsAppButton label="Parla con Chiara" testid="non-corso-whatsapp-cta" />
          <p className="mt-4 text-sm text-[#3A3532]/80">
            Raccontami cosa stai vivendo e capiamo insieme se questo percorso può essere adatto a te.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
